import React from "react"
import {
  I18nStateProvider,
  useI18nState,
} from "@myvp/shared/src/i18n/i18n-state-provider"
import { ThemeProvider, type Theme } from "@emotion/react"
import { theme as defaultTheme } from "@myvp/shared/src/styles/theme"
import { useQuery } from "@tanstack/react-query"
import { getLocaleScript } from "@myvp/shared/src/i18n/get-locale-script"
import { AlertList } from "@myvp/shared/src/components/alert-list"
import { getDateFnsLanguageLocaleFile } from "@myvp/shared/src/i18n/locale-helpers"
import defaultDateFnsLocale from "date-fns/locale/en-US"

const TouchScreenFocusOnMountContext = React.createContext({
  allowTouchScreenFocusOnMount: true,
})

export const TouchScreenFocusOnMountProvider = (props: {
  allowTouchScreenFocusOnMount: boolean
  children?: React.ReactNode
}) => {
  const value = React.useMemo(
    () => ({
      allowTouchScreenFocusOnMount: props.allowTouchScreenFocusOnMount,
    }),
    [props.allowTouchScreenFocusOnMount]
  )

  return (
    <TouchScreenFocusOnMountContext.Provider value={value}>
      {props.children}
    </TouchScreenFocusOnMountContext.Provider>
  )
}

export const useTouchScreenFocusOnMount = () => {
  return React.useContext(TouchScreenFocusOnMountContext)
    .allowTouchScreenFocusOnMount
}

export interface UIStateProvidersProps {
  locale: string
  updateIntl: (locale?: string) => void
  messages: Record<string, string>
  dateFnsLocale: import("date-fns").Locale
  rtl: boolean
  theme?: Theme
  children?: React.ReactNode
}

/**
 * React context providers for shared components to render with the same:
 *
 * - locale
 * - text direction
 *
 * Can be used directly to set these for a part of the UI.
 */
export const UIStateProviders = (props: UIStateProvidersProps) => {
  const theme = React.useMemo(() => {
    const baseTheme = props.theme ?? defaultTheme
    const localeScript = getLocaleScript(props.locale)
    return { ...baseTheme, isRTL: props.rtl, localeScript }
  }, [props.theme, props.locale, props.rtl])

  return (
    <I18nStateProvider
      locale={props.locale}
      dateFnsLocale={props.dateFnsLocale}
      updateIntl={props.updateIntl}
      messages={props.messages}
      rtl={props.rtl}
    >
      <ThemeProvider theme={theme}>{props.children}</ThemeProvider>
    </I18nStateProvider>
  )
}

export const RootUIStateProviders = (props: UIStateProvidersProps) => {
  return (
    <UIStateProviders {...props}>
      <SetRootProperties />
      <AlertList>{props.children}</AlertList>
    </UIStateProviders>
  )
}

export type UIProvidersProps = {
  loadMessages: (locale: string) => Promise<Record<string, string>>
  textDirection?: (locale: string) => "ltr" | "rtl"
  theme?: Theme
  staticTestMessages?: Record<string, string>
  children?: React.ReactNode
  queryKey?: string
} & (
  | { resolveLocale: (localeOverride?: string) => string }
  | { locale: string }
)

export const UIProviders = (props: UIProvidersProps) => {
  const [localeState, setLocaleState] = React.useState(
    "resolveLocale" in props ? props.resolveLocale : props.locale
  )
  const locale = "locale" in props ? props.locale : localeState
  // NOTE: {stale,cache}Time aren't needed, `import` caches those requests
  const { data } = useQuery({
    notifyOnChangeProps: ["data"],
    queryKey: ["i18n-provider", locale, props.queryKey],
    queryFn: async () => {
      const [messages, dateFnsLocale] = await Promise.all([
        props.loadMessages(locale),
        getDateFnsLanguageLocaleFile(locale),
      ])
      return { messages, dateFnsLocale: dateFnsLocale ?? defaultDateFnsLocale }
    },
    enabled: !props.staticTestMessages,
    // keep the previously loaded messages while the new locale loads.
    placeholderData: (previousData) => previousData,
  })

  const updateIntl = React.useCallback(
    (localeOverride?: string) => {
      if ("resolveLocale" in props) {
        const getLocale = props.resolveLocale
        const locale = getLocale(localeOverride)
        setLocaleState(locale)
      }
    },
    [props]
  )

  const messages = props.staticTestMessages ?? data?.messages
  const dateFnsLocale = props.staticTestMessages
    ? defaultDateFnsLocale
    : data?.dateFnsLocale
  return !messages || !dateFnsLocale ? null : (
    <UIStateProviders
      messages={messages}
      dateFnsLocale={dateFnsLocale}
      locale={locale}
      updateIntl={updateIntl}
      rtl={props.textDirection?.(locale) === "rtl"}
      theme={props.theme}
    >
      {props.children}
    </UIStateProviders>
  )
}

/**
 * Includes providers for an application and makes global modification to affect the
 * entire document.
 *
 * @see UIProviders
 */
export const RootUIProviders = (props: UIProvidersProps) => {
  return (
    <UIProviders {...props}>
      <SetRootProperties />
      <AlertList>{props.children}</AlertList>
    </UIProviders>
  )
}
const SetRootProperties = (): null => {
  const { rtl, locale } = useI18nState()
  React.useLayoutEffect(() => {
    document.documentElement.dir = rtl ? "rtl" : "ltr"
    document.documentElement.setAttribute(
      "data-font-face",
      getLocaleScript(locale) ?? ""
    )
  }, [rtl, locale])

  return null
}

export const getLocaleRegionProps = (
  args: {
    locale: string
    dir: "rtl" | "ltr"
  },
  otherProps?: { className?: string }
) => {
  return {
    ...otherProps,
    className: ["locale-region", otherProps?.className]
      .filter((cn) => !!cn)
      .join(" "),
    dir: args.dir,
    lang: args.locale,
    "data-font-face": getLocaleScript(args.locale),
  }
}

export const useLocaleRegionProps = () => {
  const { locale, rtl } = useI18nState()

  return (otherProps?: React.ComponentPropsWithoutRef<"div">) => {
    return getLocaleRegionProps(
      { locale, dir: rtl ? "rtl" : "ltr" },
      otherProps
    )
  }
}

export const LocaleRegion = (props: React.ComponentProps<"div">) => {
  const getLocaleRegionProps = useLocaleRegionProps()
  return <div {...getLocaleRegionProps(props)}>{props.children}</div>
}
